import { createTheme, Theme } from '@mui/material/styles';

export const designTokens = {
  colors: {
    primary: {
      main: '#132ea8',
      dark: '#0056b3',
      light: '#42a5f5',
    },
    secondary: {
      main: '#acc3ff',
      dark: '#071142',
      light: '#3d4785',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
    },
    warning: {
      main: '#f68102',  
      light: '#ffa726',
      dark: '#e65100',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#b71c1c',
    },
    background: {
      blue: '#132ea8',
      lightBlue: '#acc3ff',
      yellow: '#ffde02',
      brightYellow: '#fbe320',
      orange: '#e8e810',
      lightOrange: '#f68102',
      grey: '#9b9b9b',
      mediumGrey: '#403f3f',
      paper: '#ffffff',
      default: '#fafafa',
      lightGrey: '#f2f2f2',
      highlight: 'lightyellow',
      white: '#fafafa',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
      light: '#f2f2f2'
    },
    row_background: {
      light: "#f2f2f2",
      dark: "#acc3ff",
      highlight: "#fcfc80"
    },
    match: {
      slight_yes: '#83f28f',
      strong_yes: '#2e7d32',
      slight_no: '#fadbd8',
      strong_no: '#d32f2f',
    },
    progress: [
      '#FFE500',
      '#FFFF00',
      '#E3FF02',
      '#ADFF94',
      '#13F100',
      '#02F7D2',
      '#07D5B5',
      '#20C973',
      '#1FA7A3',
      '#96DEFB',
      '#55AAE0',
      '#267FF7',
      '#2C69C5',
      '#072FE6',
      '#423FD5',
      '#8C6DF2',
    ]
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },
  typography: {
    fontFamily: {
      primary: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      heading: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      body: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    },
    fontSize: {
      xs: 12,
      sm: 14,
      md: 16,
      lg: 18,
      xl: 20,
      xxl: 24,
      xxxl: 32,
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      bold: 700,
    },
  },
  borderRadius: {
    xs: 1,
    sm: 3,
    md: 5,
    lg: 8,
    xl: 12,
  },
  borderWidth: {
    xxs: "0.5px",
    xs: "1px",
    sm: "1.5px",
    md: "2px",
    lg: "2.5px",
    xl: "3px",
  },
  shadows: {
    light: '0 1px 3px rgba(0,0,0,0.12)',
    medium: '0 4px 6px rgba(0,0,0,0.1)',
    heavy: '0 10px 25px rgba(0,0,0,0.15)',
  },
} as const;

// fontFamily.heading: '"Cormorant", Georgia, "Times New Roman", serif',

// Extend MUI theme interface
declare module '@mui/material/styles' {
  interface Palette {
    row_background: {
      blue: string;
      yellow: string;
      grey: string;
    };
  }
  interface PaletteOptions {
    row_background?: {
      blue?: string;
      yellow?: string;
      grey?: string;
    };
  }
  interface Theme {
    customSpacing: typeof designTokens.spacing;
    customShadows: typeof designTokens.shadows;
  }
  interface ThemeOptions {
    customSpacing?: typeof designTokens.spacing;
    customShadows?: typeof designTokens.shadows;
  }
}

export const createAppTheme = (mode: 'light' | 'dark' = 'light'): Theme => {
  return createTheme({
    palette: {
      mode,
      primary: designTokens.colors.primary,
      secondary: designTokens.colors.secondary,
      background: {
        default: mode === 'light' ? designTokens.colors.background.default : '#121212',
        paper: mode === 'light' ? designTokens.colors.background.paper : '#1e1e1e',
      },
      text: {
        primary: mode === 'light' ? designTokens.colors.text.primary : '#ffffff',
        secondary: mode === 'light' ? designTokens.colors.text.secondary : '#b3b3b3',
      },
      row_background: designTokens.colors.background,
    },
    typography: {
      fontFamily: designTokens.typography.fontFamily.primary,
      h1: {
        fontFamily: designTokens.typography.fontFamily.heading,
        fontSize: designTokens.typography.fontSize.xxl,
        fontWeight: designTokens.typography.fontWeight.bold,
        lineHeight: 1.2,
        marginBottom: designTokens.spacing.md,
      },
      h2: {
        fontFamily: designTokens.typography.fontFamily.heading,
        fontSize: designTokens.typography.fontSize.xl,
        fontWeight: designTokens.typography.fontWeight.bold,
        lineHeight: 1.3,
        marginBottom: designTokens.spacing.md,
      },
      h3: {
        fontFamily: designTokens.typography.fontFamily.heading,
        fontSize: designTokens.typography.fontSize.lg,
        fontWeight: designTokens.typography.fontWeight.medium,
        lineHeight: 1.4,
        marginBottom: designTokens.spacing.sm,
      },
      body1: {
        fontFamily: designTokens.typography.fontFamily.body,
        fontSize: designTokens.typography.fontSize.md,
        lineHeight: 1.5,
        marginBottom: designTokens.spacing.sm,
      },
      body2: {
        fontFamily: designTokens.typography.fontFamily.body,
        fontSize: designTokens.typography.fontSize.sm,
        lineHeight: 1.4,
      },
      caption: {
        fontFamily: designTokens.typography.fontFamily.body,
        fontSize: designTokens.typography.fontSize.xs,
        lineHeight: 1.3,
      },
    },
    spacing: designTokens.spacing.sm, // Base spacing unit (8px)
    shape: {
      borderRadius: designTokens.borderRadius.md,
    },
    customSpacing: designTokens.spacing,
    customShadows: designTokens.shadows,
    components: {
      // Global component overrides
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: designTokens.borderRadius.md,
            padding: `${designTokens.spacing.sm}px ${designTokens.spacing.md}px`,
            fontSize: designTokens.typography.fontSize.sm,
            fontWeight: designTokens.typography.fontWeight.medium,
            boxShadow: designTokens.shadows.light,
            '&:hover': {
              boxShadow: designTokens.shadows.medium,
            },
          },
          // Color-specific hover styles
          containedPrimary: {
            '&:hover': {
              backgroundColor: designTokens.colors.primary.dark,
              color: designTokens.colors.background.paper,
            },
          },
          containedSecondary: {
            '&:hover': {
              backgroundColor: designTokens.colors.secondary.light,
              color: designTokens.colors.text.light,
            },
          },
          containedInfo: {
            '&:hover': {
              backgroundColor: designTokens.colors.primary.light,
              color: designTokens.colors.background.paper,
            },
          },
          containedSuccess: {
            '&:hover': {
              backgroundColor: designTokens.colors.success.main,
              color: designTokens.colors.background.paper,
            },
          },
          containedWarning: {
            '&:hover': {
              backgroundColor: designTokens.colors.warning.main,
              color: designTokens.colors.background.paper,
            },
          },
          containedError: {
            '&:hover': {
              backgroundColor: designTokens.colors.error.main,
              color: designTokens.colors.background.paper,
            },
          },
          sizeSmall: {
            padding: `${designTokens.spacing.xs}px ${designTokens.spacing.sm}px`,
            fontSize: designTokens.typography.fontSize.xs,
          },
          sizeLarge: {
            padding: `${designTokens.spacing.md}px ${designTokens.spacing.lg}px`,
            fontSize: designTokens.typography.fontSize.md,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.lg,
            boxShadow: designTokens.shadows.light,
            padding: designTokens.spacing.md,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: designTokens.borderRadius.md,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.md,
            fontSize: designTokens.typography.fontSize.xs,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: designTokens.shadows.medium,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: designTokens.colors.background.mediumGrey,
          },
        },
      },
    },
  });
};
